import type { ReactNode } from "react"

import { ButtonBase as MUIButtonBase, type ButtonBaseProps } from "@mui/material"
import { alpha } from "@mui/material/styles"

import type { FC } from ".."

const StyledButtonBase: FC<ButtonBaseProps> = ({ sx, ...props }) => (
  <MUIButtonBase
    sx={[
      (theme) => ({
        "height": { xs: 52, md: 62 },
        "width": { xs: 52, md: 62 },
        "border": "1px solid transparent",
        "borderRadius": 9999,
        "lineHeight": 1,
        "transition": theme.transitions.create(["background-color", "border-color", "box-shadow"]),

        "&:disabled": {
          bgcolor: `${theme.palette["neutral.200"].main}!important`,
          borderColor: "transparent!important",
          boxShadow: "none!important",
          color: theme.palette["neutral.700"].main + "!important",
          cursor: "not-allowed",
          pointerEvents: "auto",
        },
      }),
      ...(Array.isArray(sx) ? sx : [sx]),
    ]}
    {...props}
  />
)

const DefaultBtn: FC<ButtonBaseProps> = ({ sx, ...props }) => (
  <StyledButtonBase
    sx={[
      (theme) => ({
        "bgcolor": theme.palette["secondary.200"].main,
        "boxShadow": `1px 1px 2px ${alpha(theme.palette["secondary.800"].main, 0.2)}`,
        "color": theme.palette["secondary.800"].main,

        "&:hover, &:focus-visible": {
          bgcolor: theme.palette["secondary.300"].main,
          boxShadow: `1px 1px 2px ${alpha(theme.palette["secondary.800"].main, 0.4)}`,
        },
      }),
      ...(Array.isArray(sx) ? sx : [sx]),
    ]}
    {...props}
  />
)

const SecondaryBtn: FC<ButtonBaseProps> = ({ sx, ...props }) => (
  <StyledButtonBase
    sx={[
      (theme) => ({
        "borderColor": alpha(theme.palette["neutral.900"].main, 0.08),
        "color": theme.palette["neutral.700"].main,

        "&:hover, &:focus-visible": {
          bgcolor: theme.palette["neutral.100"].main,
          borderColor: theme.palette["neutral.300"].main,
        },
      }),
      ...(Array.isArray(sx) ? sx : [sx]),
    ]}
    {...props}
  />
)

const TertiaryBtn: FC<ButtonBaseProps> = ({ sx, ...props }) => (
  <StyledButtonBase
    sx={[
      (theme) => ({
        "bgcolor": theme.palette["secondary.800"].main,
        "color": theme.palette["neutral.100"].main,

        "&:hover, &:focus-visible": {
          bgcolor: theme.palette["secondary.900"].main,
        },
      }),
      ...(Array.isArray(sx) ? sx : [sx]),
    ]}
    {...props}
  />
)

const QuaternaryBtn: FC<ButtonBaseProps> = ({ sx, ...props }) => (
  <StyledButtonBase
    sx={[
      (theme) => ({
        "fontWeight": 500,
        "bgcolor": theme.palette["primary.800"].main,
        "color": theme.palette["neutral.100"].main,

        "&:hover, &:focus-visible": {
          bgcolor: theme.palette["primary.900"].main,
        },
      }),
      ...(Array.isArray(sx) ? sx : [sx]),
    ]}
    {...props}
  />
)

const NeutralSecondaryBtn: FC<ButtonBaseProps> = ({ sx, ...props }) => (
  <StyledButtonBase
    sx={[
      (theme) => ({
        "bgcolor": theme.palette["neutral.100"].main,
        "boxShadow": `1px 1px 2px ${alpha(theme.palette["secondary.800"].main, 0.15)}`,
        "color": theme.palette["secondary.800"].main,

        "&:hover, &:focus-visible": {
          bgcolor: theme.palette["secondary.300"].main,
          boxShadow: `1px 1px 2px ${alpha(theme.palette["secondary.800"].main, 0.4)}`,
        },
      }),
      ...(Array.isArray(sx) ? sx : [sx]),
    ]}
    {...props}
  />
)

const NeutralTertiaryBtn: FC<ButtonBaseProps> = ({ sx, ...props }) => (
  <StyledButtonBase
    sx={[
      (theme) => ({
        "bgcolor": theme.palette["neutral.100"].main,
        "boxShadow": `1px 1px 2px ${theme.palette["tertiary.800"].main}`,

        "&:hover, &:focus-visible": {
          bgcolor: theme.palette["tertiary.800"].main,
          boxShadow: `1px 1px 2px ${theme.palette["tertiary.900"].main}`,
        },
      }),
      ...(Array.isArray(sx) ? sx : [sx]),
    ]}
    {...props}
  />
)

// Pick (or Omit) props that are used within MUIButton Props
// type ButtonBaseProps = Pick<MUIButtonProps, "disabled" | "onClick">

export interface IconButtonProps extends ButtonBaseProps {
  variant?: "default" | "secondary" | "tertiary" | "quaternary" | "base"
  href?: string
  icon: ReactNode
  target?: string
}

const IconButton: FC<IconButtonProps> = ({
  icon,
  href,
  variant = "default",
  target = "_default",
  ...props
}) => {
  const commonProps: {
    component: "a" | "button"
    href?: string
    target?: string
  } = {
    component: href ? "a" : "button",
    href,
    ...(href && { target }),
  }

  switch (variant) {
    case "secondary":
      return (
        <SecondaryBtn {...commonProps} {...props}>
          {icon}
        </SecondaryBtn>
      )
    case "tertiary":
      return (
        <TertiaryBtn {...commonProps} {...props}>
          {icon}
        </TertiaryBtn>
      )
    case "quaternary":
      return (
        <QuaternaryBtn {...commonProps} {...props}>
          {icon}
        </QuaternaryBtn>
      )
    case "base":
      return (
        <StyledButtonBase {...commonProps} {...props}>
          {icon}
        </StyledButtonBase>
      )
    default:
      return (
        <DefaultBtn {...commonProps} {...props}>
          {icon}
        </DefaultBtn>
      )
  }
}
export default IconButton
